/**
 * The client-only **"Show blood"** setting (Blood splatter, PRD #372, Slice A): a
 * single checkbox in the Settings panel (`#blood-checkbox` in `index.html`) that
 * gates the {@link ./render/bloodLayer.BloodLayer} decals for squeamish players and
 * streamers. Purely local render state - it persists in its own `britbank.showBlood`
 * localStorage key and touches no socket, so nothing here is on the wire.
 *
 * Like `music.ts`/`settings.ts`, this module owns **only** the DOM wiring; the
 * markup lives in `index.html`, and it touches no Pixi, so it is unit-testable
 * headlessly (jsdom). The persisted choice is pushed to the renderer at startup (via
 * `onChange`), so a player who turned blood off last session keeps it off before any
 * hit lands. Default **on** (blood shows) when nothing is stored.
 */

/** Local-storage key the player's "Show blood" choice persists under. */
const SHOW_BLOOD_KEY = 'britbank.showBlood';

/** The window owning a document (the live `window` in the app, the jsdom one in tests). */
function windowOf(doc: Document): Window & typeof globalThis {
  return (doc.defaultView ?? window) as Window & typeof globalThis;
}

/**
 * Read the saved "Show blood" choice, defaulting to **on** (blood shown) when
 * nothing is stored, the value is unrecognised, or storage is unavailable.
 */
function loadShowBlood(doc: Document): boolean {
  try {
    const raw = windowOf(doc).localStorage.getItem(SHOW_BLOOD_KEY);
    // Only an explicit '0' turns blood off; anything else (incl. absent) is on.
    return raw !== '0';
  } catch {
    return true;
  }
}

/** Persist the "Show blood" choice so it survives a reload/reconnect. */
function saveShowBlood(doc: Document, show: boolean): void {
  try {
    windowOf(doc).localStorage.setItem(SHOW_BLOOD_KEY, show ? '1' : '0');
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/** Options for {@link initBlood}; `doc` is injected by tests. */
export interface BloodOptions {
  /**
   * Apply the "Show blood" choice to the renderer. Called once at startup with the
   * persisted choice (so a saved off takes effect immediately) and again on every
   * toggle. `main.ts` wires this to `GameRenderer.setBloodEnabled`.
   */
  onChange: (show: boolean) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts`/tests drive the blood setting through. */
export interface BloodHandle {
  /** Whether blood is currently shown. */
  isEnabled(): boolean;
  /** Set the "Show blood" state programmatically (reflects, persists, and notifies). */
  setEnabled(show: boolean): void;
}

export function initBlood(opts: BloodOptions): BloodHandle {
  const doc = opts.doc ?? document;
  const checkbox = doc.getElementById('blood-checkbox') as HTMLInputElement | null;

  let show = loadShowBlood(doc);

  /** Paint the checkbox to match the current state. */
  const reflect = (): void => {
    if (checkbox) checkbox.checked = show;
  };

  /** Move the choice to `next`, optionally persisting, then reflect + notify the renderer. */
  const apply = (next: boolean, persist: boolean): void => {
    show = next;
    if (persist) saveShowBlood(doc, show);
    reflect();
    opts.onChange(show);
  };

  checkbox?.addEventListener('change', () => apply(checkbox.checked, true));

  // Paint the saved state and push it to the renderer up front, so a player who
  // turned blood off last session has it off before login.
  reflect();
  opts.onChange(show);

  return {
    isEnabled: () => show,
    setEnabled: (next) => apply(next, true),
  };
}
